import cx from 'classnames';
import _ from 'lodash';
import Numeral from 'numeral';
import { slugify } from '../../../../../../shared/common/slugify';
import { safeParseInt } from '../../../../../../shared/common/utils';
import { navigateTo } from '../../../../../../shared/frontend/libs/history';
import { plural } from '../../../../../../shared/frontend/libs/utils';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import WowIcon from '../../../../../../shared/frontend/scripts/wow/components/WowIcon';
import { AllFatedAffixes, FATED_AFFIXES, XCC_BOSS_AFFIXES } from './xccEventData';

type TVersusWidgetSingleGuildProps = any & {
  theme: string;
  difficulty: string;
  side: 'left' | 'right' | 'center';
  guild: any;
  progressDisplay: string;
  lifelines: string[];
  totalEarned: number;
  hidePercents?: boolean;
  hidePulls?: boolean;
  hideEarnings?: boolean;
  totalGuilds: number;
  noPadding?: boolean;
  hideBossIcons?: boolean;
};

export function VersusWidgetSingleGuild(props: TVersusWidgetSingleGuildProps) {
  const guildName = props.guild.displayName;
  let guildLogo = props.guild.logo;

  const hideBossData = props.pullCount === 0 || !props.latestEncounter;
  const hideBossIcons = props.hideBossIcons;

  const xccAffixes =
    (props.theme === 'xcc' &&
      props.latestEncounter && [...XCC_BOSS_AFFIXES[props.raid.slug][props.latestEncounter.boss.slug]]) ||
    null;

  if (xccAffixes && props.lifelines.includes(props.latestEncounter.boss.slug)) {
    // expand the affixes by the other possible affixes
    const missingAffixes = _.difference(AllFatedAffixes, xccAffixes);
    xccAffixes.unshift(...missingAffixes);
  }

  const bossProgressColumn = hideBossData ? null : (
    <div key="boss-progress" className="slds-col versus-widget-bosses">
      <div className="widget-anim--fade-in-fast">
        <div className="versus-widget-boss-icon-container">
          {!hideBossIcons && (
            <div className="slds-grid slds-grid--align-center slds-grid--vertical-align-end">
              <div className="slds-col slds-grow-none">
                <img
                  className="versus-widget-boss-icon widget-anim--fade-in-fast"
                  style={{ height: 80 }}
                  src={asset(`/images/${props.raid.slug}/portraits/${props.latestEncounter?.boss.slug}.png`)}
                />
              </div>
            </div>
          )}
          {xccAffixes && (
            <div className="versus-widget-affix-container">
              {xccAffixes.map((afxIndex: number, iconIndex: number) => (
                <div
                  key={`${props.latestEncounter.boss.slug}-${afxIndex}`}
                  style={iconIndex > 0 ? { marginTop: 5 } : {}}
                >
                  <WowIcon
                    className="versus-widget-affix-icon widget-anim--fade-in"
                    size="x-small"
                    icon={FATED_AFFIXES[afxIndex].icon}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        {!props.hidePercents && (
          <div className="versus-widget-boss-progress">
            {/* {props.encountersDefeated}/{props.numEncounters}<br /> */}
            {props.isKilled ? 'DEFEATED' : props.progressDisplay}
          </div>
        )}
        {!props.hidePulls && <div className="versus-widget-boss-pullcount">Pull {props.pullCount}</div>}
      </div>
    </div>
  );

  const guildColumn = (
    <div
      key="guild"
      className="slds-col versus-widget-guild-container slds-p-horizontal--small"
      {...(props.latestEncounter.loggedEncounterId
        ? {
            style: { cursor: 'pointer' },
            onClick: (domEvent: any) =>
              navigateTo(
                `/raid-logged-encounters/${props.raid.slug}/${props.latestEncounter.loggedEncounterId}`,
                // `${props.guild.path}/raid-encounters/mythic/${props.raid.slug}/${props.latestEncounter.boss.slug}`,
                domEvent
              )
          }
        : {})}
    >
      {props.theme === 'xcc' && (
        <div className="versus-widget-guild-label">
          {props.totalEarned > 0 && !props.hideEarnings ? (
            `$${Numeral(props.totalEarned).format('0,0')} USD`
          ) : (
            <>&nbsp;</>
          )}
        </div>
      )}
      <img src={guildLogo} alt="Guild Logo" style={{ marginBottom: 10 }} className="versus-widget-guild-logo" />
      <div className="versus-widget-guild-name">{guildName}</div>
    </div>
  );

  const showLogoWithName = safeParseInt(props.qParams.logo_with_name as string, 0) !== 0;

  const columns: any[] = [];
  if (props.side === 'left') {
    columns.push(bossProgressColumn, guildColumn);
  } else if (props.side === 'right') {
    columns.push(guildColumn, bossProgressColumn);
  } else {
    columns.push(
      <div
        key="guild"
        className={cx(
          'versus-widget-guild-container',
          { 'slds-p-top--medium slds-p-horizontal--small': !props.noPadding },
          props.numGuilds > 4 ? 'slds-size--1-of-4' : 'slds-col'
        )}
      >
        <div
          {...(props.latestEncounter.loggedEncounterId
            ? {
                style: { cursor: 'pointer' },
                onClick: (domEvent: any) =>
                  navigateTo(
                    `/raid-logged-encounters/${props.raid.slug}/${props.latestEncounter.loggedEncounterId}`,
                    // `${props.guild.path}/raid-encounters/mythic/${props.raid.slug}/${props.latestEncounter.boss.slug}`,
                    domEvent
                  )
              }
            : {})}
        >
          <div className={cx('versus-widget-guild-name', showLogoWithName ? 'versus-widget-guild-name-with-logo' : '')}>
            {showLogoWithName ? (
              <div className="slds-grid slds-grid--vertical-align-center slds-grid--align-center">
                <div className={cx('versus-widget-guild-name-column--logo slds-col slds-grow-none')}>
                  <img
                    src={guildLogo}
                    alt="Guild Logo"
                    className="versus-widget-guild-logo versus-widget-guild-logo--default-side"
                  />
                </div>
                <div className="slds-col versus-widget-guild-name-column--name">{guildName}</div>
              </div>
            ) : (
              guildName
            )}
          </div>
          {!showLogoWithName && (
            <img
              src={guildLogo}
              alt="Guild Logo"
              className="versus-widget-guild-logo versus-widget-guild-logo--default-side"
            />
          )}
        </div>
        <div className="versus-widget-boss-progress">
          {/* {props.encountersDefeated}/{props.numEncounters}<br /> */}
          {props.isKilled ? (
            <span style={{ color: '#ffbd0a' }}>Defeated</span>
          ) : (
            <span>{props.totalGuilds <= 3 ? `Best: ${props.progressDisplay}` : props.progressDisplay}</span>
          )}
        </div>
        <div className="versus-widget-boss-pullcount">
          {plural('Pull', props.pullCount)} {props.pullCount}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cx(
        `versus-widget-side-${props.side}`,
        `versus-guild--${slugify(guildName)}`,
        'slds-grid slds-grid--vertical-align-center'
      )}
    >
      {columns}
    </div>
  );
}
